import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { PaperPlaneRight, Package, Camera, X, FilePdf, Paperclip } from "@phosphor-icons/react";
import { compressImage } from "@/lib/image";

export default function Chat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState(null);
  const [attachment, setAttachment] = useState(null); // { dataUri, name, kind: 'image'|'pdf' }
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const pdfRef = useRef(null);
  const [finderToken] = useState(localStorage.getItem(`finder_token_${id}`));
  const endRef = useRef(null);
  const pollRef = useRef(null);
  const lastTsRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  useEffect(() => {
    if (loading) return;
    const fetchConv = async () => {
      try {
        const { data } = await api.get(`/conversations/${id}`, { params: { finder_token: finderToken || undefined } });
        setConv(data.conversation);
        setMessages(data.messages);
        seenIdsRef.current = new Set((data.messages || []).map(m => m.message_id));
        if (data.messages?.length) lastTsRef.current = data.messages[data.messages.length - 1].created_at;
      } catch (e) {
        toast.error("Conversation inaccessible");
        navigate("/");
      }
    };
    fetchConv();
    // Mark as read (best-effort, only for authenticated users)
    if (user) {
      api.post(`/conversations/${id}/mark-read`).catch(() => { /* silent */ });
    }
    // poll new messages every 3s — use refs to avoid stale closure
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/conversations/${id}/messages`, { params: { since: lastTsRef.current || undefined, finder_token: finderToken || undefined } });
        if (data && data.length) {
          const fresh = data.filter(m => !seenIdsRef.current.has(m.message_id));
          if (fresh.length) {
            fresh.forEach(m => seenIdsRef.current.add(m.message_id));
            lastTsRef.current = fresh[fresh.length - 1].created_at;
            setMessages(m => [...m, ...fresh]);
            // Live mark-read as new messages arrive while viewing
            if (user) api.post(`/conversations/${id}/mark-read`).catch(() => {});
          }
        }
      } catch (e) { console.warn("Chat polling failed", e); }
    }, 3000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [id, loading, finderToken]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() && !photo && !attachment) return;
    if (sending) return;
    setSending(true);
    try {
      const payload = {
        text,
        photo: attachment?.kind === "image" ? attachment.dataUri : photo,
        attachment: attachment?.dataUri || null,
        attachment_name: attachment?.name || null,
        finder_token: finderToken,
      };
      const { data } = await api.post(`/conversations/${id}/messages`, payload);
      // Track this message to avoid duplication when polling
      if (data?.message_id) {
        seenIdsRef.current.add(data.message_id);
        if (data.created_at) lastTsRef.current = data.created_at;
      }
      setMessages(m => [...m, data]);
      setText("");
      setPhoto(null);
      setAttachment(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi");
    } finally { setSending(false); }
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Photo trop lourde (max 15 Mo)"); return; }
    try {
      const compressed = await compressImage(file);
      setAttachment({ dataUri: compressed, name: file.name, kind: "image" });
    } catch { toast.error("Impossible de lire cette photo"); }
    e.target.value = "";
  };

  const handlePdf = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.includes("pdf")) { toast.error("Format PDF uniquement"); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error("PDF trop lourd (max 3 Mo)"); return; }
    const reader = new FileReader();
    reader.onload = () => setAttachment({ dataUri: reader.result, name: file.name, kind: "pdf" });
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  if (!conv) return <Layout><div className="p-12">Chargement...</div></Layout>;

  const isOwner = conv.role === "owner";
  const otherFirstName = isOwner ? conv.finder_first_name : conv.owner_first_name;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to={user ? "/conversations" : "/mes-messages"} className="text-stone-500 hover:text-orange-600 text-sm font-bold">← Retour</Link>

        <div className="bg-white rounded-3xl border border-rose-100 mt-4 overflow-hidden flex flex-col" style={{ height: "70vh" }}>
          <div className="px-6 py-4 border-b border-rose-100 bg-rose-50/40">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-display text-lg font-bold text-stone-800" data-testid="chat-other-name">{otherFirstName}</p>
                <p className="text-xs text-stone-500">Conversation privée • prénoms uniquement</p>
              </div>
              <Link to={`/relais/${id}`}>
                <Button variant="outline" size="sm" className="rounded-full border-rose-200 text-rose-700 font-bold" data-testid="choose-relais-button">
                  <Package size={16} weight="bold" className="mr-1" /> Envoi du doudou
                </Button>
              </Link>
            </div>
            {conv.relais && (
              <div className="mt-3 text-xs bg-emerald-50 text-emerald-700 rounded-xl px-3 py-2">
                📦 Point relais choisi : <strong>{conv.relais.name}</strong> — {conv.relais.address}, {conv.relais.zip} {conv.relais.city}
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3" data-testid="chat-messages">
            {messages.map(m => {
              if (m.sender === "system") return (
                <div key={m.message_id} className="text-center text-xs text-stone-500 bg-stone-50 rounded-full px-3 py-1 mx-auto w-fit">{m.text}</div>
              );
              const mine = (isOwner && m.sender === "owner") || (!isOwner && m.sender === "finder");
              return (
                <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] px-4 py-2.5 ${mine ? "bg-orange-500 text-white rounded-t-2xl rounded-bl-2xl rounded-br-sm" : "bg-rose-50 text-stone-800 rounded-t-2xl rounded-br-2xl rounded-bl-sm"}`}>
                    <p className="text-xs font-bold opacity-80 mb-0.5">{m.sender_first_name}</p>
                    {m.photo && (
                      <a href={m.photo} target="_blank" rel="noopener noreferrer" className="block">
                        <img src={m.photo} alt="Photo partagée" className="rounded-xl max-w-full max-h-64 object-cover mb-1.5" data-testid={`chat-message-photo-${m.message_id}`} />
                      </a>
                    )}
                    {m.attachment && (m.attachment.startsWith("data:application/pdf") || (m.attachment_name || "").toLowerCase().endsWith(".pdf")) && (
                      <a href={m.attachment} download={m.attachment_name || "document.pdf"} target="_blank" rel="noopener noreferrer"
                         className={`flex items-center gap-2 mb-1.5 px-3 py-2 rounded-xl ${mine ? "bg-white/20 hover:bg-white/30" : "bg-white hover:bg-rose-100 border border-rose-200"}`}
                         data-testid={`chat-message-pdf-${m.message_id}`}>
                        <FilePdf size={22} weight="duotone" className={mine ? "text-white" : "text-rose-500"} />
                        <span className="text-sm font-semibold truncate">{m.attachment_name || "Document PDF"}</span>
                      </a>
                    )}
                    {m.text && <p className="text-sm whitespace-pre-wrap">{m.text}</p>}
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>

          {(attachment || photo) && (
            <div className="border-t border-rose-100 px-3 pt-3 bg-rose-50/40 flex items-center gap-3" data-testid="chat-photo-preview">
              {attachment?.kind === "pdf" ? (
                <div className="w-16 h-16 rounded-xl bg-rose-100 grid place-items-center text-rose-500">
                  <FilePdf size={28} weight="duotone" />
                </div>
              ) : (
                <img src={attachment?.dataUri || photo} alt="Aperçu" className="w-16 h-16 rounded-xl object-cover" />
              )}
              <span className="text-sm text-stone-600 flex-1 truncate">
                {attachment?.kind === "pdf" ? attachment.name : "Photo prête à envoyer"}
              </span>
              <button onClick={() => { setPhoto(null); setAttachment(null); }} className="p-1.5 rounded-full bg-white text-rose-500 hover:bg-rose-100" data-testid="chat-photo-remove" aria-label="Retirer le fichier">
                <X size={16} weight="bold" />
              </button>
            </div>
          )}

          <div className="border-t border-rose-100 p-3 flex gap-2 bg-white">
            <input type="file" accept="image/*" ref={fileRef} onChange={handlePhoto} className="hidden" data-testid="chat-photo-input" />
            <input type="file" accept="application/pdf" ref={pdfRef} onChange={handlePdf} className="hidden" data-testid="chat-pdf-input" />
            <button onClick={() => fileRef.current?.click()} className="p-3 rounded-full bg-rose-50 text-rose-600 hover:bg-rose-100 shrink-0" aria-label="Joindre une photo" data-testid="chat-photo-button">
              <Camera size={20} weight="duotone" />
            </button>
            <button onClick={() => pdfRef.current?.click()} className="p-3 rounded-full bg-rose-50 text-rose-600 hover:bg-rose-100 shrink-0" aria-label="Joindre un PDF (étiquette d'envoi)" data-testid="chat-pdf-button" title="Joindre un PDF (étiquette d'envoi)">
              <Paperclip size={20} weight="duotone" />
            </button>
            <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Votre message..." className="rounded-full py-6" data-testid="chat-input" />
            <Button onClick={send} disabled={sending} className="bg-orange-500 hover:bg-orange-600 rounded-full px-5 shrink-0" data-testid="chat-send-button"><PaperPlaneRight size={20} weight="bold" /></Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
